import React, { createContext, ReactNode, useContext } from "react";
import { toast } from "react-toastify";
import ToastIcon from "./ToastIcon";
import "./Toast.css";

export enum ToastType {
    Success = "success",
    Warning = "warning",
    Error = "error",
}

type ToastContextType = {
    showToast: (message: string, type: ToastType) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error("useToast must be used within a ToastProvider");
    }
    return context;
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    
    const showToast = (message: string, type: ToastType) => {
        const toastStyles = {
            error: {
                backgroundColor: "rgba(17, 24, 39, 0.9)", // bg-gray-900/90
                border: "2px solid #ef4444", // border-red-500
                color: "#f87171", // text-red-400
                boxShadow: "0 10px 15px -3px rgba(239, 68, 68, 0.3)", // shadow-red-500/30
            },
            warning: {
                backgroundColor: "rgba(17, 24, 39, 0.9)", // bg-gray-900/90
                border: "2px solid #eab308", // border-yellow-500
                color: "#facc15", // text-yellow-400
                boxShadow: "0 10px 15px -3px rgba(234, 179, 8, 0.3)", // shadow-yellow-500/30
            },
            success: {
                backgroundColor: "rgba(17, 24, 39, 0.9)", // bg-gray-900/90
                border: "2px solid #22c55e", // border-green-500
                color: "#4ade80", // text-green-400
                boxShadow: "0 10px 15px -3px rgba(34, 197, 94, 0.3)", // shadow-green-500/30
            },
            info: {
                backgroundColor: "rgba(17, 24, 39, 0.9)", // bg-gray-900/90
                border: "2px solid #3b82f6", // border-blue-500
                color: "#60a5fa", // text-blue-400
                boxShadow: "0 10px 15px -3px rgba(59, 130, 246, 0.3)", // shadow-blue-500/30
            },
        };

        switch (type) {
            case ToastType.Success:
                toast.success(message, {
                    icon: <ToastIcon type={ToastType.Success} />,
                    style: toastStyles.success,
                });
                break;
            case ToastType.Warning:
                toast.warning(message, {
                    icon: <ToastIcon type={ToastType.Warning} />,
                    style: toastStyles.warning,
                });
                break;
            case ToastType.Error:
                toast.error(message, {
                    icon: <ToastIcon type={ToastType.Error} />,
                    style: toastStyles.error,
                });
                break;
        }
    };

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
        </ToastContext.Provider>
    );
};
